import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { fmtLbs, today, stockBadge, stockPct, stockBarColor, Modal, AdjustModal, useForm } from '../components/helpers';

const EMPTY = { name: '', supplier: '', on_hand: '', reorder_at: '' };

export default function RawMaterials() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [editing, setEditing] = useState(null);
  const [adjusting, setAdjusting] = useState(null);
  const [form, set, setForm] = useForm(EMPTY);

  async function load() {
    const { data } = await supabase.from('raw_materials').select('*').order('name');
    setItems(data || []);
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  function openAdd() { setForm(EMPTY); setEditing(null); setModal('add'); }
  function openEdit(item) { setForm({ name: item.name, supplier: item.supplier || '', on_hand: item.on_hand, reorder_at: item.reorder_at }); setEditing(item); setModal('edit'); }

  async function save() {
    if (!form.name || form.on_hand === '' || form.reorder_at === '') { alert('Name, on hand, and reorder fields are required.'); return; }
    const payload = { name: form.name.trim(), supplier: form.supplier.trim(), on_hand: Number(form.on_hand), reorder_at: Number(form.reorder_at), updated_at: today() };
    if (editing) {
      await supabase.from('raw_materials').update(payload).eq('id', editing.id);
    } else {
      await supabase.from('raw_materials').insert(payload);
    }
    setModal(null);
    load();
  }

  async function applyAdjust(delta, reason) {
    const newVal = Math.max(0, Number(adjusting.on_hand) + delta);
    await supabase.from('raw_materials').update({ on_hand: newVal, updated_at: today() }).eq('id', adjusting.id);
    await supabase.from('inventory_adjustments').insert({ item_type: 'raw', item_id: adjusting.id, item_name: adjusting.name, adjustment: delta, reason });
    setModal(null);
    load();
  }

  async function del(id) {
    if (!window.confirm('Delete this material?')) return;
    await supabase.from('raw_materials').delete().eq('id', id);
    load();
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Raw Materials</div>
          <div className="page-sub">Resin inventory tracked by weight (lbs)</div>
        </div>
        <button className="btn btn-primary" onClick={openAdd}>+ Add Material</button>
      </div>
      <div className="tbl-wrap">
        <table>
          <thead><tr><th>Material / Resin</th><th>Supplier</th><th>On Hand</th><th>Reorder At</th><th>Status</th><th>Updated</th><th>Actions</th></tr></thead>
          <tbody>
            {loading && <tr className="empty-row"><td colSpan={7}>Loading...</td></tr>}
            {!loading && items.length === 0 && <tr className="empty-row"><td colSpan={7}>No raw materials yet.</td></tr>}
            {items.map(item => {
              const pct = stockPct(item.on_hand, item.reorder_at);
              const barColor = stockBarColor(item.on_hand, item.reorder_at);
              return (
                <tr key={item.id}>
                  <td><strong>{item.name}</strong></td>
                  <td className="text-muted">{item.supplier || '—'}</td>
                  <td className="mono">{fmtLbs(item.on_hand)}<div className="progress-wrap"><div className="progress-bar" style={{ width: pct + '%', background: barColor }} /></div></td>
                  <td className="mono">{fmtLbs(item.reorder_at)}</td>
                  <td>{stockBadge(item.on_hand, item.reorder_at)}</td>
                  <td className="text-muted" style={{ fontSize: 12 }}>{item.updated_at || '—'}</td>
                  <td>
                    <div className="flex-row">
                      <button className="btn btn-sm" onClick={() => { setAdjusting(item); setModal('adjust'); }}>+/−</button>
                      <button className="btn btn-sm" onClick={() => openEdit(item)}>Edit</button>
                      <button className="btn btn-sm btn-danger" onClick={() => del(item.id)}>Del</button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {(modal === 'add' || modal === 'edit') && (
        <Modal title={modal === 'add' ? 'Add Raw Material' : 'Edit Raw Material'} onClose={() => setModal(null)} onSave={save}>
          <div className="form-group"><label className="form-label">Material / Resin Name *</label><input className="form-input" value={form.name} onChange={set('name')} placeholder="e.g. PP Homopolymer" autoFocus /></div>
          <div className="form-group"><label className="form-label">Supplier</label><input className="form-input" value={form.supplier} onChange={set('supplier')} placeholder="e.g. LyondellBasell" /></div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">On Hand (lbs) *</label><input className="form-input mono" type="number" min="0" value={form.on_hand} onChange={set('on_hand')} placeholder="0" /></div>
            <div className="form-group"><label className="form-label">Reorder At (lbs) *</label><input className="form-input mono" type="number" min="0" value={form.reorder_at} onChange={set('reorder_at')} placeholder="0" /></div>
          </div>
        </Modal>
      )}
      {modal === 'adjust' && adjusting && (<AdjustModal item={adjusting} unit="lbs" onClose={() => setModal(null)} onSave={applyAdjust} />)}
    </div>
  );
}
